/**
 * Grading Conversion Utilities
 *
 * This module handles the transformation from task evaluations to grades
 * through a clear, sequential process:
 *
 * 1. Task Evaluations (levels) -> Performance Percentages (0-1 scale)
 * 2. Performance Percentages -> Task Performance Average (respecting dimension weights)
 * 3. Task Performance Average -> Task Grade (1-5 scale) via tenant grade projector
 * 4. Multiple Task Grades -> Module Grade (respecting task type weights)
 */

import log from '../../../common/utils/logger';

/** Legacy essay default when no projector is supplied (backward compatible). */
export const DEFAULT_ESSAY_PROJECTOR = { type: 'german_legacy_essay' };

/**
 * Round to one decimal toward a worse grade (higher number on the 1-5 scale).
 * Mirrors backend `_round_toward_worse_tenth`.
 */
const roundTowardWorseTenth = (raw) => {
  const scaled = raw * 10;
  if (Math.abs(scaled - Math.round(scaled)) < 1e-9) {
    return Math.round(raw * 10) / 10;
  }
  return Math.ceil(scaled - 1e-9) / 10;
};

/**
 * Resolve the grade projector for a task type, falling back to the tenant default.
 *
 * @param {{ gradeProjector?: object, taskGradeProjectors?: Record<string, object> }} projectors
 * @param {string} [taskType]
 * @returns {object}
 */
export const resolveGradeProjector = (projectors, taskType) => {
  const gradeProjector = projectors?.gradeProjector ?? DEFAULT_ESSAY_PROJECTOR;
  const taskGradeProjectors = projectors?.taskGradeProjectors ?? {};
  if (taskType && taskGradeProjectors[taskType]) {
    return taskGradeProjectors[taskType];
  }
  return gradeProjector;
};

/**
 * Map attainment s in [0, 1] to a local grade using a projector config.
 *
 * @param {number} performance - Attainment on 0-1 scale
 * @param {object} [projector] - Grade projector from tenant or sealed snapshot
 * @returns {number}
 */
export const projectGrade = (performance, projector) => {
  const resolved = projector ?? DEFAULT_ESSAY_PROJECTOR;

  switch (resolved.type) {
    case 'german_legacy_essay':
      if (performance < 0.5) return 5.0;
      return Math.round((7.0 - 6.0 * performance) * 10) / 10;

    case 'german_legacy_oral': {
      const raw = 5.0 - 4.0 * performance;
      if (raw > 4.0) return 5.0;
      return Math.max(1.0, Math.round(raw * 10) / 10);
    }

    case 'binary_pass_fail':
      return performance >= 0.5 ? 1.0 : 5.0;

    case 'german_banded': {
      const failAt = resolved.failAt ?? resolved.fail_at ?? 0.5;
      const topAt = resolved.topAt ?? resolved.top_at ?? 0.9;
      if (performance < failAt) return 5.0;
      if (performance >= topAt) return 1.0;
      const raw = 4.0 - ((3.0 * (performance - failAt)) / (topAt - failAt));
      return roundTowardWorseTenth(raw);
    }

    default:
      log.warn(`Unknown grade projector type "${resolved.type}", falling back to legacy essay`);
      if (performance < 0.5) return 5.0;
      return Math.round((7.0 - 6.0 * performance) * 10) / 10;
  }
};

/**
 * Format projected task grade for display (matches module grade: one decimal, e.g. `1.0`).
 *
 * @param {number|null|undefined} performance - Attainment s on 0-1 scale
 * @param {object} [projector] - Grade projector from tenant or sealed snapshot
 * @returns {string|null} Display string, or null when attainment is empty
 */
export const formatProjectedGradeDisplay = (performance, projector) => {
  if (performance === null || performance === undefined || Number.isNaN(performance)) {
    return null;
  }
  const grade = projectGrade(performance, projector);
  if (Number.isNaN(grade)) {
    return null;
  }
  return Number(grade).toFixed(1);
};

/**
 * Map aggregated attainment s back onto the nearest tenant performance band.
 * Ties (exact midpoint) take the worse (lower) band so we do not inflate.
 *
 * @param {number|null|undefined} performance
 * @param {{ id?: string, label?: string, performancePercent?: number }[]} performanceLevels
 * @returns {{ id?: string, label?: string, performancePercent: number }|null}
 */
export const performanceToNearestLevel = (performance, performanceLevels) => {
  if (performance === null || performance === undefined || Number.isNaN(performance)) {
    return null;
  }
  const levels = (performanceLevels || []).filter(
    (level) => level && typeof level.performancePercent === 'number' && !Number.isNaN(level.performancePercent),
  );
  if (levels.length === 0) {
    return null;
  }
  return levels.reduce((best, level) => {
    const dist = Math.abs(performance - level.performancePercent);
    const bestDist = Math.abs(performance - best.performancePercent);
    if (dist < bestDist - 1e-9) return level;
    if (Math.abs(dist - bestDist) <= 1e-9 && level.performancePercent < best.performancePercent) {
      return level;
    }
    return best;
  });
};

/**
 * Student-facing band label for aggregated attainment (never a percent or grade).
 *
 * @param {number|null|undefined} performance
 * @param {{ id?: string, label?: string, performancePercent?: number }[]} performanceLevels
 * @returns {string|null}
 */
export const formatPerformanceLevelDisplay = (performance, performanceLevels) => {
  const level = performanceToNearestLevel(performance, performanceLevels);
  return level?.label || null;
};

/**
 * Unwrap `{ kind: 'flat', dimensions }` so callers can pass either the tagged
 * evaluation or a bare dim map. Object.entries on the tagged shape treats
 * `kind` / `dimensions` as ratings and yields no performances.
 */
export const toDimensionRatingMap = (taskEvaluation) => {
  if (!taskEvaluation || typeof taskEvaluation !== 'object' || Array.isArray(taskEvaluation)) {
    return {};
  }
  const nested = taskEvaluation.dimensions;
  if (nested && typeof nested === 'object' && !Array.isArray(nested) && !('level' in nested)) {
    return nested;
  }
  return taskEvaluation;
};

export const convertEvaluationsToPerformances = (taskEvaluation, levelWeightsMap) => {
  const ratings = toDimensionRatingMap(taskEvaluation);
  if (!ratings || !levelWeightsMap || Object.keys(levelWeightsMap).length === 0) {
    return {};
  }

  return Object.entries(ratings).reduce((acc, [dimension, evalData]) => {
    const performances = { ...acc };
    if (dimension === 'kind' || !evalData || typeof evalData !== 'object' || Array.isArray(evalData)) {
      return performances;
    }
    const level = evalData?.level;

    if (!level || level === '' || level === 'not_applicable' || level === 'n/a') {
      performances[dimension] = null;
    } else if (level in levelWeightsMap) {
      performances[dimension] = levelWeightsMap[level];
    } else {
      log.warn(
        `Unknown performance level "${level}" for dimension "${dimension}". `
          + `Valid levels are: ${Object.keys(levelWeightsMap).join(', ')}`,
      );
      performances[dimension] = null;
    }

    return performances;
  }, {});
};

/**
 * Step 2: Calculates weighted average of performance percentages, respecting dimension max weights
 */
export const calculateTaskPerformance = (dimensionPerformances, dimensionMaxWeights = {}) => {
  const validPerformances = Object.entries(dimensionPerformances)
    .filter(([, perf]) => perf !== null && perf !== undefined)
    .reduce((acc, [dim, perf]) => {
      acc[dim] = perf;
      return acc;
    }, {});

  const numDimensions = Object.keys(validPerformances).length;

  if (numDimensions === 0) {
    return null;
  }

  if (numDimensions === 1 || !dimensionMaxWeights || Object.keys(dimensionMaxWeights).length === 0) {
    return Object.values(validPerformances).reduce((sum, perf) => sum + perf, 0) / numDimensions;
  }

  const initialWeight = 1.0 / numDimensions;
  const weights = Object.keys(validPerformances).reduce((acc, dim) => {
    acc[dim] = initialWeight;
    return acc;
  }, {});

  const constrainedDimensions = Object.entries(dimensionMaxWeights)
    .filter(
      ([dim, maxWeight]) => dim in validPerformances
        && maxWeight !== null
        && maxWeight !== undefined
        && maxWeight > 0
        && maxWeight < initialWeight,
    )
    .map(([dim, maxWeight]) => ({ dim, maxWeight }));

  if (constrainedDimensions.length === 0) {
    return Object.values(validPerformances).reduce((sum, perf) => sum + perf, 0) / numDimensions;
  }

  let remainingWeight = 1.0;
  const unconstrainedDimensions = new Set(Object.keys(validPerformances));

  constrainedDimensions.forEach(({ dim, maxWeight }) => {
    weights[dim] = maxWeight;
    remainingWeight -= maxWeight;
    unconstrainedDimensions.delete(dim);
  });

  if (unconstrainedDimensions.size > 0) {
    const weightPerDimension = Math.max(0, remainingWeight) / unconstrainedDimensions.size;
    unconstrainedDimensions.forEach((dim) => {
      weights[dim] = weightPerDimension;
    });
  }

  return Object.entries(weights).reduce((sum, [dim, weight]) => sum + validPerformances[dim] * weight, 0);
};

/**
 * Step 3: Converts a performance percentage to a grade on the 1-5 scale.
 *
 * @param {number} performance - Performance percentage (0-1 scale)
 * @param {object} [projector] - Grade projector; defaults to legacy essay when absent
 * @returns {number} Grade on 1-5 scale, or NaN if input is invalid
 */
export const convertPerformanceToGrade = (performance, projector) => {
  if (performance === null || performance === undefined || Number.isNaN(performance)) {
    return NaN;
  }

  return projectGrade(performance, projector);
};

/**
 * Canonical oral-exam dimension weights. Must stay aligned with
 * ``_default_oral_exam_rubric`` in tenant_process_settings.py — this is the
 * attainment the grade projector sees (content-heavy, drop-worst).
 */
export const DEFAULT_ORAL_EXAM_DIMENSION_WEIGHTS = {
  content: 0.40,
  depth: 0.25,
  application: 0.20,
  communication: 0.10,
  professionalism: 0.05,
};

export const ORAL_EXAM_DROP_WORST_N = 1;

const readOralExamDimLevel = (rating) => {
  if (typeof rating === 'string') return rating;
  return rating?.level ?? null;
};

/**
 * Official oral-exam attainment on [0, 1].
 * Per question: weighted dim average (missing dims do not dilute).
 * Then drop the worst question and average the rest.
 * Mirrors ``calculate_oral_exam_grade`` steps 1–4 (before the projector).
 */
export const calculateOralExamPerformance = (
  subEvaluations,
  levelWeightsMap,
  dimensionWeights = DEFAULT_ORAL_EXAM_DIMENSION_WEIGHTS,
  dropWorstN = ORAL_EXAM_DROP_WORST_N,
) => {
  if (!subEvaluations || !levelWeightsMap) {
    return null;
  }

  const rows = Array.isArray(subEvaluations)
    ? subEvaluations
    : Object.values(subEvaluations);

  const questionPerfs = [];
  rows.forEach((sub) => {
    const dimGrades = sub?.dimGrades ?? sub?.dim_grades ?? {};
    const hasWeights = dimensionWeights && Object.keys(dimensionWeights).length > 0;

    if (hasWeights) {
      let weightedSum = 0;
      let totalWeight = 0;
      Object.entries(dimGrades).forEach(([dim, rating]) => {
        const level = readOralExamDimLevel(rating);
        if (!level || level === 'not_applicable' || !(level in levelWeightsMap)) {
          return;
        }
        const weight = dimensionWeights[dim] ?? 0;
        weightedSum += levelWeightsMap[level] * weight;
        totalWeight += weight;
      });
      if (totalWeight > 0) {
        questionPerfs.push(weightedSum / totalWeight);
      }
      return;
    }

    const perfs = Object.values(dimGrades)
      .map((rating) => readOralExamDimLevel(rating))
      .filter((level) => level && level !== 'not_applicable' && level in levelWeightsMap)
      .map((level) => levelWeightsMap[level]);
    if (perfs.length > 0) {
      questionPerfs.push(perfs.reduce((sum, value) => sum + value, 0) / perfs.length);
    }
  });

  if (questionPerfs.length === 0) {
    return null;
  }

  questionPerfs.sort((a, b) => a - b);
  const remaining = questionPerfs.length > dropWorstN
    ? questionPerfs.slice(dropWorstN)
    : questionPerfs;
  return remaining.reduce((sum, value) => sum + value, 0) / remaining.length;
};

/**
 * Convenience function: Converts task evaluation directly to a task grade
 */
export const calculateTaskGrade = (
  taskEvaluation,
  levelWeightsMap,
  dimensionMaxWeights = {},
  projector = undefined,
) => {
  try {
    const performances = convertEvaluationsToPerformances(taskEvaluation, levelWeightsMap);

    const hasValidPerformances = Object.values(performances).some(
      (perf) => perf !== null && perf !== undefined && !Number.isNaN(perf),
    );

    if (!hasValidPerformances) {
      return NaN;
    }

    const taskPerformance = calculateTaskPerformance(performances, dimensionMaxWeights);
    return convertPerformanceToGrade(taskPerformance, projector);
  } catch (error) {
    log.error(`Error calculating task grade: ${error.message}`);
    return NaN;
  }
};

/**
 * Step 4: Calculates a module grade from multiple task grades
 */
export const calculateModuleGrade = (tasks, fullEvalTaskWeight = 0.5) => {
  if (!tasks || tasks.length === 0) {
    return null;
  }

  const validTasks = tasks.filter(
    (task) => task && task.grade !== null && task.grade !== undefined && !Number.isNaN(task.grade),
  );

  if (validTasks.length === 0) {
    return null;
  }

  const fullEvalTasks = validTasks.filter((task) => task.isFullEvaluation);
  const passFailTasks = validTasks.filter((task) => !task.isFullEvaluation);

  const fullEvalAverage = fullEvalTasks.length > 0 ? fullEvalTasks.reduce((sum, task) => sum + task.grade, 0) / fullEvalTasks.length : null;

  const passFailAverage = passFailTasks.length > 0 ? passFailTasks.reduce((sum, task) => sum + task.grade, 0) / passFailTasks.length : null;

  let finalGrade;
  if (fullEvalAverage !== null && passFailAverage !== null) {
    finalGrade = fullEvalAverage * fullEvalTaskWeight + passFailAverage * (1 - fullEvalTaskWeight);
  } else if (fullEvalAverage !== null) {
    finalGrade = fullEvalAverage;
  } else if (passFailAverage !== null) {
    finalGrade = passFailAverage;
  } else {
    return null;
  }

  if (finalGrade > 4.0) {
    return 5.0;
  }

  return Math.round(finalGrade * 10) / 10;
};

export const hasValidEvaluation = (evaluation) => evaluation
  && typeof evaluation === 'object'
  && Object.values(evaluation).some((dim) => dim?.level != null && dim.level !== '');

export const getEvaluationStats = (evaluations) => ({
  total: evaluations.length,
  evaluated: evaluations.filter(hasValidEvaluation).length,
  hasAny: evaluations.some(hasValidEvaluation),
  hasAll: evaluations.every(hasValidEvaluation),
});
